import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UNAUTHORIZED_EVENT, getMe, login as apiLogin, logout as apiLogout } from '../api/client';

const AuthContext = createContext(null);

// Knows who is signed in. The server decides; this only remembers the answer for the pages.
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((me) => { if (!cancelled) setUser(me); })
      .catch(() => { if (!cancelled) setUser(null); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  // Any page that gets a "sign in first" answer sends the whole app back to the login page.
  useEffect(() => {
    const onLost = () => setUser(null);
    window.addEventListener(UNAUTHORIZED_EVENT, onLost);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onLost);
  }, []);

  const signIn = useCallback(async (email, password) => {
    setUser(await apiLogin(email, password));
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // already signed out on the server
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, checking, signIn, signOut }), [user, checking, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// The signed-in user, or null outside the provider (a page rendered on its own in a test).
export function useCurrentUser() {
  const value = useContext(AuthContext);
  return value ? value.user : null;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
